import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle2, BarChart3, Users, ArrowRight, Building2, Globe, LayoutDashboard, Zap, ChevronRight, Star } from 'lucide-react';
import { db } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

export function LandingPage() {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        projectSize: '',
        country: '',
        city: '',
        company: '',
        accountType: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // 1. Save to Firestore
            await addDoc(collection(db, 'leads'), {
                ...formData,
                createdAt: new Date(),
                status: 'new'
            });

            // 2. Send Email via EmailJS
            await emailjs.send(
                'service_2gja339',
                'template_3nbsdvu',
                {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phone: formData.phone,
                    accountType: formData.accountType,
                    company: formData.company,
                    projectSize: formData.projectSize,
                    country: formData.country,
                    city: formData.city
                },
                'pUXtUhYUanSYN_b35'
            );

            setSubmitted(true);
            toast.success('¡Información enviada con éxito!');
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Hubo un error al enviar tu información. Por favor intenta de nuevo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const scrollToForm = () => {
        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-[#0B0C0E] text-white font-sans selection:bg-indigo-500/30 overflow-x-hidden">
            {/* Navbar */}
            <nav className={`fixed top-0 z-50 w-full transition-all duration-300 ${scrolled ? 'bg-[#0B0C0E]/80 backdrop-blur-md border-b border-white/5' : 'bg-transparent'}`}>
                <div className="container flex h-16 items-center justify-between px-6 md:px-8 max-w-7xl mx-auto">
                    <div className="flex items-center gap-2 font-medium tracking-tight">
                        <div className="h-6 w-6 rounded bg-white text-black flex items-center justify-center">
                            <Building2 className="h-4 w-4" />
                        </div>
                        <span className="text-lg">Bimobi</span>
                    </div>
                    <div className="flex items-center gap-6">
                        <a href="/login" className="text-sm text-gray-400 hover:text-white transition-colors">Iniciar Sesión</a>
                        <Button className="hidden md:flex bg-white text-black hover:bg-gray-200 rounded-full px-5 h-8 text-sm font-medium transition-all" onClick={scrollToForm}>
                            Solicitar Demo
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden">
                {/* Background Effects */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-indigo-500/20 blur-[120px] rounded-full opacity-50 pointer-events-none"></div>

                <div className="container px-4 md:px-8 max-w-5xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-indigo-300 mb-8 backdrop-blur-sm animate-fade-in-up">
                        <span className="flex h-2 w-2 rounded-full bg-indigo-500 mr-2 animate-pulse"></span>
                        Bimobi 2.0 ya está aquí
                        <ChevronRight className="ml-1 h-3 w-3 text-gray-400" />
                    </div>

                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-gray-400 mb-8 leading-[1.1]">
                        El nuevo estándar <br /> inmobiliario.
                    </h1>

                    <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed">
                        Gestiona inventario, finanzas y clientes con la elegancia y potencia que tu equipo merece. Diseñado para escalar desde pequeños fraccionamientos hasta grandes desarrollos corporativos.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
                        <Button size="lg" className="h-12 px-8 text-base bg-white text-black hover:bg-gray-200 rounded-full transition-all hover:scale-105" onClick={scrollToForm}>
                            Empezar ahora <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </div>

                    {/* 3D Perspective Screenshot Container */}
                    <div className="relative mx-auto w-full max-w-6xl perspective-[2000px] group">
                        <div className="relative transform transition-all duration-700 ease-out group-hover:rotate-x-2 rotate-x-12 scale-90 group-hover:scale-95">
                            {/* Glow behind */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>

                            {/* Main Image */}
                            <div className="relative rounded-xl border border-white/10 bg-[#121316] shadow-2xl overflow-hidden">
                                <div className="absolute top-0 w-full h-8 bg-[#1A1B1E] border-b border-white/5 flex items-center px-4 gap-2">
                                    <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50"></div>
                                </div>
                                <img
                                    src="/assets/screenshots/screenshot-1.png"
                                    alt="Bimobi Dashboard"
                                    className="w-full h-auto pt-8 opacity-90"
                                />
                                {/* Overlay Gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-transparent to-transparent opacity-40"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Grid - Linear Style */}
            <section className="py-32 border-t border-white/5 bg-[#0B0C0E]">
                <div className="container px-4 md:px-8 max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Un flujo de trabajo unificado</h2>
                        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                            Olvídate de las herramientas desconectadas. Bimobi sincroniza cada aspecto de tu desarrollo inmobiliario.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <FeatureCard
                            icon={<LayoutDashboard className="h-6 w-6 text-indigo-400" />}
                            title="Inventario en Tiempo Real"
                            description="Control total de tus lotes. Visualiza disponibilidad, precios y estados con un mapa interactivo que se actualiza al instante."
                        />
                        <FeatureCard
                            icon={<BarChart3 className="h-6 w-6 text-purple-400" />}
                            title="Finanzas Automatizadas"
                            description="Seguimiento automático de pagos, generación de recibos y cálculo de comisiones. Tu flujo de caja, siempre claro."
                        />
                        <FeatureCard
                            icon={<Users className="h-6 w-6 text-pink-400" />}
                            title="CRM Integrado"
                            description="Perfil detallado de cada cliente con historial de pagos, documentos y comunicación. Construye relaciones duraderas."
                        />
                    </div>
                </div>
            </section>

            {/* Secondary Screenshots - Staggered */}
            <section className="py-32 bg-[#0B0C0E] relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-900/20 via-[#0B0C0E] to-[#0B0C0E]"></div>
                <div className="container px-4 md:px-8 max-w-7xl mx-auto relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center mb-32">
                        <div className="order-2 md:order-1">
                            <div className="relative rounded-xl border border-white/10 bg-[#121316] shadow-2xl overflow-hidden transform -rotate-2 hover:rotate-0 transition-all duration-500">
                                <img src="/assets/screenshots/screenshot-2.png" alt="Reports" className="w-full h-auto opacity-90" />
                            </div>
                        </div>
                        <div className="order-1 md:order-2">
                            <div className="inline-flex items-center rounded-full border border-purple-500/30 bg-purple-500/10 px-3 py-1 text-xs font-medium text-purple-400 mb-6">
                                <Zap className="mr-2 h-3 w-3" />
                                Insights
                            </div>
                            <h3 className="text-3xl font-bold mb-4">Toma decisiones basadas en datos</h3>
                            <p className="text-gray-400 text-lg leading-relaxed">
                                Dashboards financieros que te muestran la salud de tu proyecto en tiempo real. Proyecciones de flujo de caja, eficiencia de cobranza y más.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
                        <div>
                            <div className="inline-flex items-center rounded-full border border-pink-500/30 bg-pink-500/10 px-3 py-1 text-xs font-medium text-pink-400 mb-6">
                                <Star className="mr-2 h-3 w-3" />
                                Experiencia
                            </div>
                            <h3 className="text-3xl font-bold mb-4">Diseñado para la velocidad</h3>
                            <p className="text-gray-400 text-lg leading-relaxed">
                                Cada interacción en Bimobi ha sido optimizada para ser rápida y fluida. Desde el registro de un pago hasta la consulta de un estado de cuenta.
                            </p>
                        </div>
                        <div>
                            <div className="relative rounded-xl border border-white/10 bg-[#121316] shadow-2xl overflow-hidden transform rotate-2 hover:rotate-0 transition-all duration-500">
                                <img src="/assets/screenshots/screenshot-3.png" alt="Movements" className="w-full h-auto opacity-90" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Form Section - Minimalist */}
            <section id="contact-form" className="py-32 relative border-t border-white/5">
                <div className="container px-4 md:px-8 max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold mb-4">Empieza a construir el futuro</h2>
                        <p className="text-gray-400">Solicita acceso a la plataforma más avanzada para desarrollos inmobiliarios.</p>
                    </div>

                    <Card className="border border-white/10 bg-[#121316] shadow-2xl">
                        <CardContent className="p-8 md:p-10">
                            {submitted ? (
                                <div className="text-center py-12">
                                    <div className="h-16 w-16 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <CheckCircle2 className="h-8 w-8" />
                                    </div>
                                    <h3 className="text-2xl font-bold mb-4">Gracias por tu interés</h3>
                                    <p className="text-gray-400 mb-8">
                                        Tu plataforma para la gestión inmobiliaria. Nuestro equipo te contactará en breve.
                                    </p>
                                    <Button variant="outline" className="border-white/10 text-white hover:bg-white/5" onClick={() => setSubmitted(false)}>
                                        Volver al inicio
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Label className="text-gray-400 text-xs uppercase tracking-wider">Nombre</Label>
                                            <Input
                                                name="firstName"
                                                required
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                className="bg-[#0B0C0E] border-white/10 focus:border-indigo-500/50 transition-colors h-10 text-white selection:bg-indigo-500/30 selection:text-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-gray-400 text-xs uppercase tracking-wider">Apellido</Label>
                                            <Input
                                                name="lastName"
                                                required
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                className="bg-[#0B0C0E] border-white/10 focus:border-indigo-500/50 transition-colors h-10 text-white selection:bg-indigo-500/30 selection:text-white"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-gray-400 text-xs uppercase tracking-wider">Email Corporativo</Label>
                                        <Input
                                            name="email"
                                            type="email"
                                            required
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className="bg-[#0B0C0E] border-white/10 focus:border-indigo-500/50 transition-colors h-10 text-white selection:bg-indigo-500/30 selection:text-white"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-gray-400 text-xs uppercase tracking-wider">Teléfono (Opcional)</Label>
                                        <Input
                                            name="phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            className="bg-[#0B0C0E] border-white/10 focus:border-indigo-500/50 transition-colors h-10 text-white selection:bg-indigo-500/30 selection:text-white"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Label className="text-gray-400 text-xs uppercase tracking-wider">País</Label>
                                            <Input
                                                name="country"
                                                required
                                                value={formData.country}
                                                onChange={handleInputChange}
                                                className="bg-[#0B0C0E] border-white/10 focus:border-indigo-500/50 transition-colors h-10 text-white selection:bg-indigo-500/30 selection:text-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-gray-400 text-xs uppercase tracking-wider">Ciudad</Label>
                                            <Input
                                                name="city"
                                                required
                                                value={formData.city}
                                                onChange={handleInputChange}
                                                className="bg-[#0B0C0E] border-white/10 focus:border-indigo-500/50 transition-colors h-10 text-white selection:bg-indigo-500/30 selection:text-white"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-gray-400 text-xs uppercase tracking-wider">Tipo de Solicitud</Label>
                                        <Select name="accountType" onValueChange={(val) => handleSelectChange('accountType', val)}>
                                            <SelectTrigger className="bg-[#0B0C0E] border-white/10 h-10 text-white">
                                                <SelectValue placeholder="Seleccionar" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121316] border-white/10 text-white">
                                                <SelectItem value="company">Empresa</SelectItem>
                                                <SelectItem value="personal">Personal</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {formData.accountType === 'company' && (
                                        <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                            <Label className="text-gray-400 text-xs uppercase tracking-wider">Nombre de la Empresa</Label>
                                            <Input
                                                name="company"
                                                value={formData.company}
                                                onChange={handleInputChange}
                                                className="bg-[#0B0C0E] border-white/10 focus:border-indigo-500/50 transition-colors h-10 text-white selection:bg-indigo-500/30 selection:text-white"
                                            />
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label className="text-gray-400 text-xs uppercase tracking-wider">Tamaño del Proyecto</Label>
                                        <Select name="projectSize" onValueChange={(val) => handleSelectChange('projectSize', val)} required>
                                            <SelectTrigger className="bg-[#0B0C0E] border-white/10 h-10 text-white">
                                                <SelectValue placeholder="Seleccionar" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121316] border-white/10 text-white">
                                                <SelectItem value="pequeno">Pequeño (&lt; 50 lotes)</SelectItem>
                                                <SelectItem value="mediano">Mediano (50 - 200 lotes)</SelectItem>
                                                <SelectItem value="grande">Grande (200 - 500 lotes)</SelectItem>
                                                <SelectItem value="corporativo">Corporativo (&gt; 500 lotes)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <Button type="submit" className="w-full h-12 bg-white text-black hover:bg-gray-200 rounded-md font-medium transition-all mt-4" disabled={isSubmitting}>
                                        {isSubmitting ? 'Enviando...' : 'Solicitar Acceso'}
                                    </Button>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 border-t border-white/5 bg-[#0B0C0E]">
                <div className="container px-4 md:px-8 max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-2 font-medium text-gray-400">
                        <Building2 className="h-4 w-4" />
                        <span>Bimobi &copy; {new Date().getFullYear()}</span>
                    </div>
                    <div className="flex gap-8">
                        <a href="#" className="text-sm text-gray-500 hover:text-white transition-colors">Twitter</a>
                        <a href="#" className="text-sm text-gray-500 hover:text-white transition-colors">GitHub</a>
                        <a href="#" className="text-sm text-gray-500 hover:text-white transition-colors">LinkedIn</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) {
    return (
        <div className="group p-6 rounded-2xl bg-[#121316] border border-white/5 hover:border-white/10 transition-all duration-300 hover:bg-white/[0.02]">
            <div className="h-10 w-10 rounded-lg bg-white/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-white/5">
                {icon}
            </div>
            <h3 className="text-lg font-medium mb-3 text-white">{title}</h3>
            <p className="text-gray-400 leading-relaxed text-sm">
                {description}
            </p>
        </div>
    );
}
